import type {
  ResponseAcademicRisk,
  YearAcademicRisk,
} from '@/domains/academicRisk/academicRiskStudent.entity';

/**
 * Use Case para filtrar y excluir años futuros de los datos de riesgo académico.
 *
 * Funcionalidad:
 * - Valida que solo se incluyan datos de años actuales o pasados
 * - Excluye automáticamente cualquier registro con año superior al actual
 * - Limpia la estructura de datos temporal para consistencia
 *
 * Responsabilidades:
 * - Obtener el año actual como referencia de validación
 * - Filtrar el objeto de años removiendo años futuros inválidos
 * - Validar y convertir años a números para comparación
 * - Retornar datos limpios con solo años válidos
 * - Mantener la estructura anidada original (año > semestre > estudiantes)
 */
export class FilterFutureYearsAcademicRiskUseCase {
  /**
   * Obtiene el año actual del sistema como referencia.
   */
  private getCurrentYear(): number {
    return new Date().getFullYear();
  }

  /**
   * Filtra el objeto de años removiendo años futuros inválidos.
   *
   * @param yearData - Objeto con estructura {año: {semestre: [estudiantes]}}
   */
  private filterValidYears(yearData: YearAcademicRisk): YearAcademicRisk {
    const currentYear = this.getCurrentYear();
    const filteredData: YearAcademicRisk = {};

    for (const year in yearData) {
      const yearNumber = Number.parseInt(year, 10);

      if (!Number.isNaN(yearNumber) && yearNumber <= currentYear) {
        filteredData[year] = yearData[year];
      }
    }

    return filteredData;
  }

  /**
   * Ejecuta el filtrado excluyendo años futuros de los datos.
   *
   * @param apiResponse - Respuesta completa de la API con estructura año > semestre > estudiantes
   */
  execute(apiResponse: ResponseAcademicRisk | null): ResponseAcademicRisk {
    if (!apiResponse?.data) {
      return { data: {} };
    }

    const filteredData = this.filterValidYears(apiResponse.data);

    return {
      data: filteredData,
    };
  }
}
